import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import Header from "../../../components/Header";
import { useLocation, useNavigate, useParams } from "react-router";
import { getJobColumns } from "./JobTableColumn";
import { useEffect, useState } from "react";
import { useGetJobItemsQuery } from "./api/job-detail.generated";
import JobFilter, { JobFilter as JobFilterType } from "./job-filter";
const JobDetail = () => {
  const theme = useTheme();
  const colors = theme.palette;
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const { title } = location.state || {};
  const navigate = useNavigate();
  const columns: GridColDef[] = getJobColumns(name, navigate);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(undefined);
  const [jobsCache, setJobsCache] = useState<Record<number, any[]>>({});
  const [filters, setFilters] = useState<JobFilterType[]>([]);
  const getDefaultSortField = (jobName: string | undefined) => {
    switch (jobName) {
      case "documents":
      case "bulk-valuation":
        return "createdAt";
      case "data-sync":
        return "createdDate";
      default:
        return "createdAt";
    }
  };
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: getDefaultSortField(name),
      sort: "desc",
    },
  ]);
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (model.page > paginationModel.page) {
      const nextLastEvaluatedKey = getJobItemResponse?.getJobItems?.lastEvaluatedKey;
      console.log("Last Evaluated Key: ", nextLastEvaluatedKey);
      if (nextLastEvaluatedKey) {
        if (name === "bulk-valuation" || name === "documents") {
          setLastEvaluatedKey(nextLastEvaluatedKey);
        } else {
          if (!nextLastEvaluatedKey.id) return;
          const keyValue = nextLastEvaluatedKey.id.substring(5, nextLastEvaluatedKey.id.length - 1);
          setLastEvaluatedKey({ id: keyValue });
        }
      }
    } else if (model.page < paginationModel.page && jobsCache[model.page]) {
      setLastEvaluatedKey(undefined);
    }
    if (model.pageSize != paginationModel.pageSize) {
      setLastEvaluatedKey(undefined);
      setJobsCache({});
      setPaginationModel({
        pageSize: paginationModel.pageSize,
        page: 0,
      });
    }
    setPaginationModel(model);
  };
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length === 0) {
      const currentSort = sortModel[0]?.sort;
      setSortModel([
        {
          field: getDefaultSortField(name),
          sort: currentSort === "desc" ? "asc" : "desc",
        },
      ]);
    } else {
      setSortModel(newSortModel);
    }
    setLastEvaluatedKey(undefined);
    setJobsCache({});
    setPaginationModel({ ...paginationModel, page: 0 });
  };
  const handleFiltersChange = (newFilters: JobFilterType[]) => {
    setFilters(newFilters);
    setLastEvaluatedKey(undefined);
    setJobsCache({});
    setPaginationModel({ ...paginationModel, page: 0 });
  };
  useEffect(() => {
    if (!name) {
      navigate("/jobs");
    }
  }, [name, navigate]);
  const filterInput = filters.map((filter) => ({
    field: filter.column,
    operator: filter.operator,
    value: filter.value,
  }));
  const sortInput =
    sortModel.length > 0
      ? {
        field: sortModel[0].field,
        order: sortModel[0].sort?.toUpperCase() || "DESC",
      }
      : {
        field: getDefaultSortField(name),
        order: "DESC",
      };
  const { data: getJobItemResponse, isFetching } = useGetJobItemsQuery({
    jobName: name || "",
    limit: paginationModel.pageSize,
    lastEvaluatedKey: lastEvaluatedKey,
    filters: filterInput.length > 0 ? filterInput : undefined,
    sortOrder: sortInput.order,
  });
  const getJobTableData = (getJobItemResponse: any) => {
    if (getJobItemResponse?.getJobItems?.bulkValuationTableData)
      return getJobItemResponse?.getJobItems?.bulkValuationTableData;
    else if (getJobItemResponse?.getJobItems?.dataSyncTableData)
      return getJobItemResponse?.getJobItems?.dataSyncTableData;
    else if (getJobItemResponse?.getJobItems?.documentProcessTableData)
      return getJobItemResponse?.getJobItems?.documentProcessTableData;
  };
  useEffect(() => {
    if (getJobTableData(getJobItemResponse)) {
      const currentPage = paginationModel.page;
      setJobsCache((prev) => ({
        ...prev,
        [currentPage]: getJobTableData(getJobItemResponse) || [],
      }));
    }
  }, [getJobItemResponse, paginationModel.page]);
  const getJobItems = (name: string | undefined) => {
    const currentPage = paginationModel.page;
    if (jobsCache[currentPage]) {
      return jobsCache[currentPage];
    }
    switch (name) {
      case "documents":
        return getJobItemResponse?.getJobItems?.documentProcessTableData || [];
      case "bulk-valuation":
        return getJobItemResponse?.getJobItems?.bulkValuationTableData || [];
      case "data-sync":
        return getJobItemResponse?.getJobItems?.dataSyncTableData || [];
      default:
        return [];
    }
  };
  const jobs = getJobItems(name);
  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title={title} subtitle={""} />
        <Button color="primary" variant="outlined" onClick={() => navigate(-1)}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Go Back
          </Typography>
        </Button>
      </Box>
      <JobFilter jobType={name} onFiltersChange={handleFiltersChange} />
      <Box
        m="0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: `1px solid ${colors.cardBorderColor}`,
            borderRadius: "2px",
          },
          "& .MuiDataGrid-cell": {},
          "& .name-column--cell": {
            color: colors.grey[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-toolbarContainer": {
            backgroundColor: colors.cardBackground,
            borderBottom: `1px solid ${colors.cardBorderColor}`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]}`,
          },
          "& .MuiDataGrid-paper": {
            backgroundColor: `${colors.blueAccent[700]} !important`,
          },
        }}
      >
        <DataGrid
          rows={jobs}
          columns={columns}
          loading={isFetching}
          density="compact"
          slots={{ toolbar: customToolBar }}
          pagination
          paginationModel={paginationModel}
          paginationMode="server"
          rowCount={getJobItemResponse?.getJobItems?.totalItems || 0}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
        />
      </Box>
    </Box>
  );
};

const customToolBar = () => {
  return (
    <GridToolbarContainer>
      <Box sx={{ display: "flex", mt: 1, mb: 1, gap: 1 }} >
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector/>
        <GridToolbarExport/>
      </Box>
    </GridToolbarContainer>
  );
};
export default JobDetail;
